import React, { useEffect, useState } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { Recipe } from "@/types/recipe";
import { useRecipeById } from '@/hooks/useRecipeById';
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { AdaptiveModal } from '@/components/ui/AdaptiveModal';
import { useRecipeConversion } from '@/hooks/useRecipeConversion';
import { useRecipeForm } from '@/hooks/useRecipeForm';
import { useUser } from '@/contexts/UserContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { isMobileDevice } from '@/lib/utils';
import RecipeDetail from '@/components/recipe/RecipeDetail';
import RecipeFeedbackSection from '@/components/recipe/RecipeFeedbackSection';
import RecipeForm from '@/components/recipe/RecipeForm';
import QuickRecipeForm from '@/components/recipe/QuickRecipeForm';
import DeleteRecipeDialog from '@/components/recipe-collection/DeleteRecipeDialog';
import { PageLayout } from '@/components/layout';
import { Button } from '@/components/ui/button';
import { Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const RecipePage = () => {
  const { id } = useParams<{ id: string; }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useUser();
  const isMobile = useIsMobile();
  const [showLoadingSkeleton, setShowLoadingSkeleton] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  
  // Use React Query for recipe fetching with caching
  const { data: recipe, isLoading, error, refetch } = useRecipeById(id);
  
  // Detect if this is a quick recipe
  const isQuickRecipe = recipe?.isQuickRecipe || recipe?.tags?.includes('quick-meal') || false;
  
  // Delay showing loading skeleton to prevent flash for cached/fast loads
  useEffect(() => {
    if (isLoading) {
      const timer = setTimeout(() => {
        setShowLoadingSkeleton(true);
      }, 200);
      
      return () => {
        clearTimeout(timer);
        setShowLoadingSkeleton(false);
      };
    } else {
      setShowLoadingSkeleton(false);
    }
  }, [isLoading]);

  const {
    displayedStructuredIngredients,
    displayedInstructions,
    preferredUnits,
    handleUnitToggle
  } = useRecipeConversion(recipe);

  const {
    isSubmitting,
    isProcessingImage,
    handleSubmit,
    handleDelete
  } = useRecipeForm(recipe, () => {
    setIsEditDialogOpen(false);
    refetch();
  });

  const [isDeleting, setIsDeleting] = useState(false);

  // RLS only returns recipes the current user's family can access.
  const canEdit = Boolean(recipe && user && isAuthenticated);
  const canDelete = Boolean(recipe && user && recipe.user_id === user.id);

  // Handle scrolling to hash fragments after page loads
  useEffect(() => {
    if (!isLoading && location.hash) {
      const timer = setTimeout(() => {
        const element = document.querySelector(location.hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [isLoading, location.hash]);

  const handleEdit = () => {
    if (isMobileDevice()) {
      // Use React Router navigation for fast SPA navigation on mobile
      navigate(`/edit-recipe/${id}`);
    } else {
      // Use modal for web
      setIsEditDialogOpen(true);
    }
  };

  const handleDeleteClick = () => {
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await handleDelete();
      setIsDeleteDialogOpen(false);
    } catch (error) {
      console.error('Delete failed:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRecipeUpdated = () => {
    setIsEditDialogOpen(false);
    refetch();
  };
  
  if (isLoading || showLoadingSkeleton) {
    return (
      <PageLayout title="Loading Recipe..." description="Please wait while we fetch the recipe details">
        <div className="space-y-4">
          <Skeleton className="h-10 w-3/4" />
          <Skeleton className="h-4 w-1/4" />
          <Skeleton className="h-20 w-full" />
          <div className="grid grid-cols-3 gap-4">
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
          <Skeleton className="h-60 w-full" />
        </div>
      </PageLayout>
    );
  }

  if (!recipe) {
    return (
      <PageLayout title="Recipe Not Found" description="The recipe you're looking for doesn't exist or has been removed">
        <div className="text-center py-12">
          <p className="text-muted-foreground mb-6">Please check the URL or try searching for another recipe.</p>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout 
      title={recipe.title} 
      description={recipe.description}
      variant="recipe"
      maxWidth="max-w-4xl"
    >
      <RecipeDetail 
        recipe={recipe} 
        structuredIngredients={displayedStructuredIngredients} 
        instructions={displayedInstructions} 
        preferredUnits={preferredUnits} 
        onUnitToggle={handleUnitToggle} 
        isQuickRecipe={isQuickRecipe}
        canEdit={canEdit}
        canDelete={canDelete}
        onEdit={handleEdit}
        onDelete={handleDeleteClick}
      />
      
      <Separator className="my-8" />

      <RecipeFeedbackSection recipeId={id || ''} />

      {!isMobileDevice() && (
        <AdaptiveModal
          isOpen={isEditDialogOpen}
          onClose={() => setIsEditDialogOpen(false)}
          title={`Edit ${isQuickRecipe ? 'Quick Recipe' : 'Recipe'}`}
          size="xl"
          scrollable={true}
        >
          {isQuickRecipe ? (
            <QuickRecipeForm recipe={recipe} onRecipeUpdated={handleRecipeUpdated} />
          ) : (
            <RecipeForm 
              recipe={recipe} 
              onSubmit={handleSubmit} 
              onCancel={() => setIsEditDialogOpen(false)} 
              isLoading={isSubmitting}
              isProcessingImage={isProcessingImage}
            />
          )}
        </AdaptiveModal>
      )}

      <DeleteRecipeDialog
        isOpen={isDeleteDialogOpen}
        isDeleting={isDeleting}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirmDelete={handleDeleteConfirm}
      />
    </PageLayout>
  );
};

export default RecipePage;
